import { useCallback, useEffect, useState, Fragment } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { Link } from "react-router-dom";
import { adminService } from "../../features/admin/service";
import type {
  AiGenerationItem,
  AiGenerationTotals,
  DealFlyerGenerationItem,
} from "../../features/admin/types";
import { env } from "../../config/env";
import { formatISTDateTime } from "../../utils/dateUtils";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";

type AssetFilter = "all" | "flyer" | "itinerary" | "video" | "deal_flyers";

const ASSET_TABS: { key: AssetFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "flyer", label: "Flyers" },
  { key: "itinerary", label: "Itineraries" },
  { key: "video", label: "Videos" },
  { key: "deal_flyers", label: "Deal Flyers" },
];

type DatePreset = "all" | "1h" | "24h" | "7d" | "30d" | "custom";

const DATE_PRESETS: { key: DatePreset; label: string }[] = [
  { key: "all", label: "All time" },
  { key: "1h", label: "Past 1 hour" },
  { key: "24h", label: "Past 24 hours" },
  { key: "7d", label: "Past 7 days" },
  { key: "30d", label: "Past 30 days" },
  { key: "custom", label: "Custom range" },
];

const PRESET_MS: Record<Exclude<DatePreset, "all" | "custom">, number> = {
  "1h": 60 * 60 * 1000,
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
};

/** Local datetime-local input value (yyyy-MM-ddTHH:mm) → ISO string, or "" through. */
const localToIso = (v: string): string | undefined => (v ? new Date(v).toISOString() : undefined);

/** Relative /uploads/ paths are served from the backend; everything else
 *  (full http(s) URLs, presigned S3 links) is used as-is. */
const resolveMediaUrl = (u?: string): string | undefined => {
  if (!u) return undefined;
  if (u.startsWith("/uploads/")) return `${env.apiBaseUrl}${u}`;
  return u;
};

const isPdfMedia = (u: string): boolean => /\.pdf(\?|$)/i.test(u);
const isDocMedia = (u: string): boolean => /\.docx?(\?|$)/i.test(u);

function extFromUrl(url: string): string {
  const match = /\.([a-zA-Z0-9]{2,5})(?:\?|$)/.exec(url.split("/").pop() || "");
  return match ? match[1] : "png";
}

function slugifyTitle(title: string | undefined): string {
  const base = (title || "asset").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return base || "asset";
}

/** Triggers a save-as with a title-based filename. A plain `<a download>`
 *  can't rename or force-download a cross-origin (S3) URL — the attribute is
 *  ignored cross-origin, so the browser just navigates to it. Instead we ask
 *  the backend for a presigned URL with Content-Disposition: attachment
 *  already baked in (same endpoint AdminGalleryReviewPage uses); navigating
 *  to that isn't subject to CORS, and S3 sends the attachment header itself. */
async function downloadAsset(url: string, filename: string) {
  try {
    const downloadUrl = await adminService.getMediaDownloadUrl(url, filename);
    const a = document.createElement("a");
    a.href = downloadUrl;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (e) {
    console.error("download failed", e);
  }
}

/** Sequential so the browser doesn't swallow the 2nd..nth navigation as a
 *  popup — each download is a presigned-URL navigation, and firing them all in
 *  the same tick gets every one but the first blocked. */
async function downloadAll(items: { url: string; filename: string }[]) {
  for (const it of items) {
    await downloadAsset(it.url, it.filename);
    await new Promise((r) => setTimeout(r, 400));
  }
}

/** Download control for a generated asset. A session that regenerated has more
 *  than one output, so pass `urls` — the button then becomes a small menu
 *  ("Download all" / "Download 1" / ...). With a single output (or just `url`)
 *  it renders and behaves exactly as the plain button always has. */
const DownloadButton: React.FC<{ url?: string; urls?: string[]; filename: string; className?: string }> = ({ url, urls, filename, className }) => {
  const [open, setOpen] = useState(false);
  const list = (urls && urls.length > 0 ? urls : [url]).filter((u): u is string => Boolean(u));
  const btnClass = className || "mt-1 px-2 py-1 text-[11px] font-medium text-indigo-300 hover:bg-indigo-500/10 rounded-md transition-colors border border-indigo-500/20 whitespace-nowrap";
  if (list.length === 0) return null;
  // The caller's filename already carries the first output's extension; for the
  // rest, re-derive it per URL so a mixed-format session still saves correctly.
  const nameFor = (u: string, i: number): string => {
    if (list.length === 1) return filename;
    const dot = filename.lastIndexOf(".");
    const stem = dot > 0 ? filename.slice(0, dot) : filename;
    return `${stem}-${i + 1}.${extFromUrl(u)}`;
  };
  if (list.length === 1) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); downloadAsset(list[0], filename); }}
        title={`Download as ${filename}`}
        className={btnClass}
      >
        Download
      </button>
    );
  }
  const items = list.map((u, i) => ({ url: u, filename: nameFor(u, i) }));
  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        title={`Download ${list.length} files`}
        className={btnClass}
      >
        Download ▾
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div className="absolute left-0 z-20 mt-1 min-w-[120px] rounded-md border border-gray-800 bg-gray-900 py-1 shadow-lg">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen(false); downloadAll(items); }}
              className="block w-full px-3 py-1 text-left text-[11px] text-indigo-300 hover:bg-indigo-500/10 whitespace-nowrap"
            >
              Download all
            </button>
            {items.map((it, i) => (
              <button
                key={it.url}
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpen(false); downloadAsset(it.url, it.filename); }}
                className="block w-full px-3 py-1 text-left text-[11px] text-gray-300 hover:bg-indigo-500/10 whitespace-nowrap"
              >
                Download {i + 1}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

type PreviewTarget = { urls: string[]; startIndex: number; label: string; kind: "image" | "pdf" | "doc" | "video" };

/** Thumbnail for a source asset or generated flyer. Clicking opens an in-page
 *  preview overlay instead of navigating to another tab/page — works for
 *  images, PDFs (embedded inline) and docx (opened via Office's web viewer,
 *  still inside the overlay). Pass `images` (instead of `url`) when a session
 *  generated more than one output — the thumbnail shows the first one plus a
 *  "1/3"-style count badge, and the preview opens as a slider over all of them. */
const MediaThumb: React.FC<{
  url?: string;
  images?: string[];
  label: string;
  onPreview: (t: PreviewTarget) => void;
  forceKind?: PreviewTarget["kind"];
}> = ({ url, images, label, onPreview, forceKind }) => {
  const resolvedList = (images && images.length > 0 ? images : [url])
    .map(resolveMediaUrl)
    .filter((u): u is string => Boolean(u));
  const resolved = resolvedList[0];
  if (!resolved) {
    return (
      <div className="w-20">
        <div className="w-20 h-24 rounded-lg bg-gray-800/60 border border-gray-800 flex items-center justify-center text-[10px] text-gray-600">
          none
        </div>
        <p className="text-[10px] text-gray-600 mt-1 text-center">{label}</p>
      </div>
    );
  }
  const kind: PreviewTarget["kind"] = forceKind || (isPdfMedia(resolved) ? "pdf" : isDocMedia(resolved) ? "doc" : "image");
  return (
    <div className="w-20">
      <button
        type="button"
        onClick={() => onPreview({ urls: resolvedList, startIndex: 0, label, kind })}
        className="relative block w-full"
      >
        {kind === "pdf" ? (
          <div className="w-20 h-24 rounded-lg bg-gray-800/60 border border-gray-800 flex items-center justify-center text-[10px] text-indigo-400 hover:border-indigo-500/50 transition-colors">
            PDF
          </div>
        ) : kind === "doc" ? (
          <div className="w-20 h-24 rounded-lg bg-gray-800/60 border border-gray-800 flex items-center justify-center text-[10px] text-indigo-400 hover:border-indigo-500/50 transition-colors">
            DOC
          </div>
        ) : kind === "video" ? (
          <video
            src={resolved}
            muted
            className="w-20 h-24 object-contain bg-gray-950 rounded-lg border border-gray-800 hover:border-indigo-500/50 transition-colors"
          />
        ) : (
          <img
            src={resolved}
            alt={label}
            className="w-20 h-24 object-contain bg-gray-950 rounded-lg border border-gray-800 hover:border-indigo-500/50 transition-colors"
          />
        )}
        {resolvedList.length > 1 && (
          <span className="absolute top-1 right-1 rounded-full bg-black/70 text-white text-[9px] font-semibold px-1.5 py-0.5">
            {resolvedList.length}
          </span>
        )}
      </button>
      <p className="text-[10px] text-gray-600 mt-1 text-center">{label}</p>
    </div>
  );
};

/** In-page overlay that previews an image, PDF, or docx without leaving the
 *  admin page. PDFs render via the browser's native inline viewer; docx uses
 *  Office's web viewer (falls back to a direct-open link if that's blocked).
 *  When `target.urls` has more than one entry (a session that regenerated
 *  more than once), it opens as a slider with prev/next + a position counter. */
const MediaPreviewModal: React.FC<{ target: PreviewTarget; onClose: () => void }> = ({ target, onClose }) => {
  const [idx, setIdx] = useState(target.startIndex);
  useEffect(() => setIdx(target.startIndex), [target]);

  const multiple = target.urls.length > 1;
  const url = target.urls[idx];
  const prev = () => setIdx((i) => (i - 1 + target.urls.length) % target.urls.length);
  const next = () => setIdx((i) => (i + 1) % target.urls.length);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div
        className="relative bg-gray-900 border border-gray-800 rounded-xl max-w-4xl w-full max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <span className="text-sm text-gray-300">
            {target.label}
            {multiple && <span className="text-gray-500 ml-2">{idx + 1} / {target.urls.length}</span>}
          </span>
          <div className="flex items-center gap-3">
            {/* Downloads whatever the slider is showing right now, not just the
                first item — a regenerated session opens here as a slider. */}
            <DownloadButton
              url={url}
              filename={`${slugifyTitle(target.label)}${multiple ? `-${idx + 1}` : ""}.${extFromUrl(url)}`}
              className="px-2 py-1 text-xs font-medium text-indigo-300 hover:bg-indigo-500/10 rounded-md transition-colors border border-indigo-500/20 whitespace-nowrap"
            />
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-indigo-400 hover:text-indigo-300"
            >
              Open in new tab
            </a>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-white text-lg leading-none"
            >
              ×
            </button>
          </div>
        </div>
        <div className="relative flex-1 min-h-0 bg-black/40 flex items-center justify-center">
          {multiple && (
            <>
              <button
                type="button"
                onClick={prev}
                aria-label="Previous"
                className="absolute left-2 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next"
                className="absolute right-2 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
              >
                ›
              </button>
            </>
          )}
          {target.kind === "image" ? (
            <img src={url} alt={target.label} className="max-w-full max-h-[75vh] object-contain" />
          ) : target.kind === "video" ? (
            <video src={url} controls autoPlay className="max-w-full max-h-[75vh]" />
          ) : target.kind === "pdf" ? (
            <iframe src={url} title={target.label} className="w-full h-[75vh]" />
          ) : (
            <iframe
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`}
              title={target.label}
              className="w-full h-[75vh]"
            />
          )}
        </div>
      </div>
    </div>
  );
};

const assetBadge = (assetType: string) => {
  if (assetType === "flyer") return "bg-indigo-500/10 text-indigo-400";
  if (assetType === "itinerary") return "bg-cyan-500/10 text-cyan-400";
  if (assetType === "video") return "bg-purple-500/10 text-purple-400";
  if (assetType === "deal_flyer") return "bg-amber-500/10 text-amber-400";
  return "bg-gray-500/10 text-gray-400";
};

const roleColor = (role: string) => {
  if (role === "seller") return "bg-cyan-500/10 text-cyan-400";
  if (role === "super_admin") return "bg-amber-500/10 text-amber-400";
  return "bg-emerald-500/10 text-emerald-400";
};

const nfmt = (n: number) => (n ?? 0).toLocaleString();
const usd = (n: number) => `$${(n ?? 0).toFixed(4)}`;
const inr = (n: number) => `₹${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AdminAiGenerationsPage: React.FC = () => {
  const [assetType, setAssetType] = useState<AssetFilter>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const [items, setItems] = useState<AiGenerationItem[]>([]);
  const [dealFlyers, setDealFlyers] = useState<DealFlyerGenerationItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState<AiGenerationTotals | null>(null);
  const [inrRate, setInrRate] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewTarget | null>(null);

  const [datePreset, setDatePreset] = useState<DatePreset>("all");
  const [dateMenuOpen, setDateMenuOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [assetType, datePreset, customFrom, customTo]);

  const fetchGenerations = useCallback(() => {
    let dateFrom: string | undefined;
    let dateTo: string | undefined;
    if (datePreset === "custom") {
      dateFrom = localToIso(customFrom);
      dateTo = localToIso(customTo);
    } else if (datePreset !== "all") {
      dateFrom = new Date(Date.now() - PRESET_MS[datePreset]).toISOString();
    }

    setLoading(true);
    if (assetType === "deal_flyers") {
      return adminService
        .getDealFlyerGenerations({
          page,
          page_size: pageSize,
          search: debouncedSearch || undefined,
          include_records: true,
          date_from: dateFrom,
          date_to: dateTo,
        })
        .then((res) => {
          setDealFlyers(res.items);
          setTotal(res.total);
          setTotals(res.totals);
          setInrRate(res.inr_rate);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
    return adminService
      .getAiGenerations({
        asset_type: assetType,
        page,
        page_size: pageSize,
        search: debouncedSearch || undefined,
        include_records: true,
        date_from: dateFrom,
        date_to: dateTo,
      })
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
        setTotals(res.totals);
        setInrRate(res.inr_rate);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [assetType, page, pageSize, debouncedSearch, datePreset, customFrom, customTo]);

  useEffect(() => {
    fetchGenerations();
  }, [fetchGenerations]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <AdminPageHeader
        title="AI Generations"
        subtitle={
          <>
            Flyers, itineraries &amp; videos generated across all users — who generated, tokens, and cost
            {inrRate ? ` (USD→INR @ ${inrRate})` : ""}
          </>
        }
        actions={<RefreshButton onRefresh={fetchGenerations} />}
      />

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Generations</p>
          <p className="text-2xl font-bold text-white">{nfmt(totals?.count ?? 0)}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Cost (USD)</p>
          <p className="text-2xl font-bold text-emerald-400">{usd(totals?.cost_usd ?? 0)}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Cost (INR)</p>
          <p className="text-2xl font-bold text-amber-400">{inr(totals?.cost_inr ?? 0)}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Total Tokens</p>
          <p className="text-2xl font-bold text-white">{nfmt(totals?.total_tokens ?? 0)}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">LLM Calls</p>
          <p className="text-2xl font-bold text-white">{nfmt(totals?.llm_calls ?? 0)}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">FAL Calls</p>
          <p className="text-2xl font-bold text-white">{nfmt(totals?.fal_calls ?? 0)}</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex flex-wrap gap-1 bg-gray-900 border border-gray-800 rounded-lg p-1">
          {ASSET_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setAssetType(t.key)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                assetType === t.key ? "bg-indigo-500/20 text-indigo-300" : "text-gray-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={
            assetType === "deal_flyers"
              ? "Search by deal title or dealer name…"
              : "Search by generator name or email…"
          }
          className="flex-1 min-w-[220px] max-w-sm bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500/50"
        />

        <div className="relative">
          <button
            type="button"
            onClick={() => setDateMenuOpen((o) => !o)}
            className="flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-300 hover:border-gray-700 transition-colors"
          >
            {DATE_PRESETS.find((p) => p.key === datePreset)?.label ?? "All time"}
            <span className="text-gray-600 text-xs">▾</span>
          </button>
          {dateMenuOpen && (
            <div
              className="absolute right-0 left-auto max-sm:left-1/2 max-sm:right-auto max-sm:-translate-x-1/2 mt-2 w-64 max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto bg-gray-900 border border-gray-800 rounded-lg shadow-xl z-20 p-2"
              onMouseLeave={() => setDateMenuOpen(false)}
            >
              {DATE_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => {
                    setDatePreset(p.key);
                    if (p.key !== "custom") setDateMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${
                    datePreset === p.key ? "bg-indigo-500/20 text-indigo-300" : "text-gray-400 hover:bg-gray-800 hover:text-white"
                  }`}
                >
                  {p.label}
                </button>
              ))}
              {datePreset === "custom" && (
                <div className="mt-2 pt-2 border-t border-gray-800 space-y-2">
                  <label className="block">
                    <span className="text-[10px] text-gray-500 uppercase tracking-wide">From</span>
                    <input
                      type="datetime-local"
                      value={customFrom}
                      onChange={(e) => setCustomFrom(e.target.value)}
                      style={{ colorScheme: "dark" }}
                      className="mt-1 w-full bg-gray-950 border border-gray-800 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none focus:border-indigo-500/50"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] text-gray-500 uppercase tracking-wide">To</span>
                    <input
                      type="datetime-local"
                      value={customTo}
                      onChange={(e) => setCustomTo(e.target.value)}
                      style={{ colorScheme: "dark" }}
                      className="mt-1 w-full bg-gray-950 border border-gray-800 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none focus:border-indigo-500/50"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setDateMenuOpen(false)}
                    className="w-full mt-1 px-3 py-1.5 rounded-md text-xs font-medium bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30 transition-colors"
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        {assetType === "deal_flyers" ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Deal</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Source &amp; Flyer</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Tokens</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Calls</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Cost (USD)</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Cost (INR)</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Generated (IST)</th>
                <th className="py-3 px-4" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="py-16 text-center text-gray-500"><Spinner /></td></tr>
              ) : dealFlyers.length === 0 ? (
                <tr><td colSpan={8} className="py-16 text-center text-gray-500">No deal flyers generated yet</td></tr>
              ) : (
                dealFlyers.map((it) => {
                  const sourceAsset = it.source.image || it.source.pdf_url || it.source.document_url;
                  return (
                    <Fragment key={it.id}>
                      <tr className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors align-top">
                        <td className="py-3 px-4">
                          <Link
                            to={`/admin/deals?deal_id=${encodeURIComponent(it.deal_id)}`}
                            className="text-indigo-400 hover:text-indigo-300 text-sm font-medium block truncate max-w-[220px]"
                          >
                            {it.title || "Untitled"}
                          </Link>
                          <p className="text-gray-500 text-xs mt-1 truncate max-w-[220px]">{it.dealer.name || "—"}</p>
                          {it.status && (
                            <span className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-medium bg-gray-700/40 text-gray-400">
                              {it.status}
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-start gap-3">
                            <MediaThumb url={sourceAsset} label="Uploaded" onPreview={setPreview} />
                            <div>
                              <MediaThumb url={it.flyer || it.image_clean} label="AI flyer" onPreview={setPreview} />
                              <DownloadButton
                                url={it.flyer || it.image_clean}
                                filename={`${slugifyTitle(it.title)}-flyer.${extFromUrl(it.flyer || it.image_clean || "")}`}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right text-gray-300 whitespace-nowrap">
                          <span className="text-white">{nfmt(it.tokens.total)}</span>
                          <span className="text-gray-600 text-xs block">{nfmt(it.tokens.prompt)}p / {nfmt(it.tokens.completion)}c</span>
                        </td>
                        <td className="py-3 px-4 text-right text-gray-300 whitespace-nowrap">
                          <span className="text-white">{nfmt(it.tokens.llm_calls)}</span> LLM
                          <span className="text-gray-600 text-xs block">{nfmt(it.tokens.fal_calls)} image</span>
                        </td>
                        <td className="py-3 px-4 text-right text-emerald-400 whitespace-nowrap">{usd(it.cost.usd)}</td>
                        <td className="py-3 px-4 text-right text-amber-400 whitespace-nowrap">{inr(it.cost.inr)}</td>
                        <td className="py-3 px-4 text-gray-400 text-xs whitespace-nowrap">
                          {it.generated_at ? formatISTDateTime(it.generated_at) : "—"}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {it.records && it.records.length > 0 && (
                            <button
                              onClick={() => setExpanded(expanded === it.id ? null : it.id)}
                              className="text-xs text-indigo-400 hover:text-indigo-300"
                            >
                              {expanded === it.id ? "Hide" : `${it.records.length} calls`}
                            </button>
                          )}
                        </td>
                      </tr>
                      {expanded === it.id && it.records && (
                        <tr className="bg-gray-950/60">
                          <td colSpan={8} className="px-4 py-3">
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-gray-600">
                                    <th className="text-left py-1 px-2 font-medium">Context</th>
                                    <th className="text-left py-1 px-2 font-medium">Provider</th>
                                    <th className="text-left py-1 px-2 font-medium">Model</th>
                                    <th className="text-right py-1 px-2 font-medium">Prompt</th>
                                    <th className="text-right py-1 px-2 font-medium">Completion</th>
                                    <th className="text-right py-1 px-2 font-medium">Total</th>
                                    <th className="text-right py-1 px-2 font-medium">Cost (USD)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {it.records.map((r, i) => (
                                    <tr key={i} className="text-gray-400 border-t border-gray-800/50">
                                      <td className="py-1 px-2">{r.context ?? "—"}</td>
                                      <td className="py-1 px-2">{r.provider ?? "—"}</td>
                                      <td className="py-1 px-2 truncate max-w-[220px]">{r.model ?? "—"}</td>
                                      <td className="py-1 px-2 text-right">{nfmt(Number(r.prompt_tokens ?? 0))}</td>
                                      <td className="py-1 px-2 text-right">{nfmt(Number(r.completion_tokens ?? 0))}</td>
                                      <td className="py-1 px-2 text-right">{nfmt(Number(r.total_tokens ?? 0))}</td>
                                      <td className="py-1 px-2 text-right text-emerald-400">{usd(Number(r.cost_usd ?? 0))}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Asset</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Generated By</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Source &amp; Output</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Tokens</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Calls</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Cost (USD)</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Cost (INR)</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Generated (IST)</th>
                <th className="py-3 px-4" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="py-16 text-center text-gray-500"><Spinner /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={9} className="py-16 text-center text-gray-500">No generations found</td></tr>
              ) : (
                items.map((it) => (
                  <Fragment key={it.id}>
                    <tr className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${assetBadge(it.asset_type)}`}>
                            {it.asset_type}
                          </span>
                          {it.source === "archived" && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-700/40 text-gray-400">archived</span>
                          )}
                          {/* Which flyer flow this was — an uploaded flyer being edited, or
                              an admin template driving a fresh design. Previously
                              indistinguishable in this table. */}
                          {it.media?.flow && it.media.flow !== "from_scratch" && (
                            <span
                              className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                it.media.flow === "flyer_edit"
                                  ? "bg-amber-500/15 text-amber-300"
                                  : "bg-sky-500/15 text-sky-300"
                              }`}
                              title={
                                it.media.flow === "flyer_edit"
                                  ? "User uploaded a finished flyer and edited it"
                                  : "User picked an admin template (Make your own flyer)"
                              }
                            >
                              {it.media.flow === "flyer_edit" ? "uploaded flyer edit" : "make your own"}
                            </span>
                          )}
                        </div>
                        {it.asset_type === "deal_flyer" && it.deal_id ? (
                          <Link
                            to={`/admin/deals?deal_id=${encodeURIComponent(it.deal_id)}`}
                            className="text-indigo-400 hover:text-indigo-300 text-sm font-medium mt-1 block truncate max-w-[220px]"
                          >
                            {it.title || "Untitled"}
                          </Link>
                        ) : (
                          <p className="text-white text-sm mt-1 truncate max-w-[220px]">{it.title || "—"}</p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400 font-semibold text-xs flex-shrink-0">
                            {it.generated_by.name?.[0]?.toUpperCase() || "?"}
                          </div>
                          <div className="min-w-0">
                            <p className="text-white font-medium text-sm truncate max-w-[180px]">{it.generated_by.name || "—"}</p>
                            <p className="text-gray-500 text-xs truncate max-w-[180px]">{it.generated_by.email}</p>
                          </div>
                          {it.generated_by.role && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${roleColor(it.generated_by.role)}`}>
                              {it.generated_by.role}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {it.asset_type === "itinerary" || it.asset_type === "deal_flyer" || it.asset_type === "flyer" ? (
                          <div className="flex items-start gap-3 flex-wrap max-w-[280px]">
                            {/* Show EVERY input that fed this generation, and say which
                                flow it was. This used to render the template preview
                                *instead of* the uploaded image whenever a template id was
                                present — but a template id sticks to a deal once picked and
                                rides along into every later edit turn, so uploaded-flyer
                                edit sessions were routinely mislabelled as template runs
                                with the actual edited flyer hidden from admin entirely. */}
                            {Boolean(it.media?.source_images?.length || it.media?.source_image) && (
                              <div>
                                <div className="flex items-start gap-3 flex-wrap">
                                  {(it.media?.source_images?.length
                                    ? it.media.source_images
                                    : it.media?.source_image
                                      ? [it.media.source_image]
                                      : []
                                  ).map((src, i) => (
                                    <MediaThumb key={`src-${i}`} url={src} label="Uploaded" onPreview={setPreview} />
                                  ))}
                                </div>
                                <DownloadButton
                                  url={it.media?.source_image}
                                  urls={it.media?.source_images}
                                  filename={`${slugifyTitle(it.title)}-uploaded.${extFromUrl(it.media?.source_images?.[0] || it.media?.source_image || "")}`}
                                />
                              </div>
                            )}
                            {it.media?.template_image_url && (
                              <div>
                                <MediaThumb
                                  url={it.media.template_image_url}
                                  label={it.media.template_name ? `Template: ${it.media.template_name}` : "Template"}
                                  onPreview={setPreview}
                                />
                                <DownloadButton
                                  url={it.media.template_image_url}
                                  filename={`${slugifyTitle(it.title)}-template.${extFromUrl(it.media.template_image_url)}`}
                                />
                              </div>
                            )}
                            <div>
                              <MediaThumb
                                url={it.media?.output_image || it.media?.output_pdf}
                                images={it.asset_type === "flyer" ? it.media?.output_images : undefined}
                                label={it.asset_type === "deal_flyer" || it.asset_type === "flyer" ? "AI flyer" : "AI itinerary"}
                                onPreview={setPreview}
                              />
                              {/* Itinerary rows carry BOTH a JPEG thumbnail (output_image) and the
                                  actual PDF (output_pdf) — thumbnail falls back to output_image first
                                  for preview purposes, but download must always be the real PDF, not
                                  whichever one happens to exist first. */}
                              <DownloadButton
                                url={it.asset_type === "itinerary" ? it.media?.output_pdf : it.media?.output_image}
                                urls={it.asset_type === "flyer" ? it.media?.output_images : undefined}
                                filename={`${slugifyTitle(it.title)}-${it.asset_type}.${extFromUrl((it.asset_type === "itinerary" ? it.media?.output_pdf : it.media?.output_image) || "")}`}
                              />
                            </div>
                          </div>
                        ) : it.asset_type === "video" ? (
                          <div className="flex items-start gap-3 flex-wrap max-w-[280px]">
                            {/* A video generation can be fed up to 6 reference photos —
                                show every one of them, the same way flyer rows show
                                their uploads, instead of only the generated output. */}
                            {Boolean(it.media?.reference_images?.length) && (
                              <div>
                                <div className="flex items-start gap-3 flex-wrap">
                                  {(it.media?.reference_images?.length ? it.media.reference_images : []).map((src, i) => (
                                    <MediaThumb key={`vref-${i}`} url={src} label="Reference" onPreview={setPreview} />
                                  ))}
                                </div>
                                <DownloadButton
                                  urls={it.media?.reference_images}
                                  filename={`${slugifyTitle(it.title)}-reference.${extFromUrl(it.media?.reference_images?.[0] || "")}`}
                                />
                              </div>
                            )}
                            <div>
                              <MediaThumb
                                url={it.media?.output_video}
                                images={it.media?.output_videos}
                                label="AI video"
                                forceKind="video"
                                onPreview={setPreview}
                              />
                              <DownloadButton
                                url={it.media?.output_video}
                                urls={it.media?.output_videos}
                                filename={`${slugifyTitle(it.title)}-video.${extFromUrl(it.media?.output_video || "")}`}
                              />
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-300 whitespace-nowrap">
                        <span className="text-white">{nfmt(it.tokens.total)}</span>
                        <span className="text-gray-600 text-xs block">{nfmt(it.tokens.prompt)}p / {nfmt(it.tokens.completion)}c</span>
                      </td>
                      <td className="py-3 px-4 text-right text-gray-300 whitespace-nowrap">
                        <span className="text-white">{nfmt(it.tokens.llm_calls)}</span> LLM
                        <span className="text-gray-600 text-xs block">{nfmt(it.tokens.fal_calls)} FAL</span>
                      </td>
                      <td className="py-3 px-4 text-right text-emerald-400 whitespace-nowrap">{usd(it.cost.usd)}</td>
                      <td className="py-3 px-4 text-right text-amber-400 whitespace-nowrap">{inr(it.cost.inr)}</td>
                      <td className="py-3 px-4 text-gray-400 text-xs whitespace-nowrap">
                        {it.generated_at ? formatISTDateTime(it.generated_at) : "—"}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {it.records && it.records.length > 0 && (
                          <button
                            onClick={() => setExpanded(expanded === it.id ? null : it.id)}
                            className="text-xs text-indigo-400 hover:text-indigo-300"
                          >
                            {expanded === it.id ? "Hide" : `${it.records.length} calls`}
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded === it.id && it.records && (
                      <tr className="bg-gray-950/60">
                        <td colSpan={9} className="px-4 py-3">
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-600">
                                  <th className="text-left py-1 px-2 font-medium">Context</th>
                                  <th className="text-left py-1 px-2 font-medium">Provider</th>
                                  <th className="text-left py-1 px-2 font-medium">Model</th>
                                  <th className="text-right py-1 px-2 font-medium">Prompt</th>
                                  <th className="text-right py-1 px-2 font-medium">Completion</th>
                                  <th className="text-right py-1 px-2 font-medium">Total</th>
                                  <th className="text-right py-1 px-2 font-medium">Cost (USD)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {it.records.map((r, i) => (
                                  <tr key={i} className="text-gray-400 border-t border-gray-800/50">
                                    <td className="py-1 px-2">{r.context ?? "—"}</td>
                                    <td className="py-1 px-2">{r.provider ?? "—"}</td>
                                    <td className="py-1 px-2 truncate max-w-[220px]">{r.model ?? "—"}</td>
                                    <td className="py-1 px-2 text-right">{nfmt(Number(r.prompt_tokens ?? 0))}</td>
                                    <td className="py-1 px-2 text-right">{nfmt(Number(r.completion_tokens ?? 0))}</td>
                                    <td className="py-1 px-2 text-right">{nfmt(Number(r.total_tokens ?? 0))}</td>
                                    <td className="py-1 px-2 text-right text-emerald-400">{usd(Number(r.cost_usd ?? 0))}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
        )}

        {/* Pagination */}
        <div className="px-5 py-3 border-t border-gray-800 flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {total > 0 ? `Showing page ${page} of ${totalPages} · ${nfmt(total)} total` : "No results"}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="px-3 py-1.5 rounded-lg text-sm bg-gray-800 text-gray-300 disabled:opacity-40 hover:bg-gray-700 transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="px-3 py-1.5 rounded-lg text-sm bg-gray-800 text-gray-300 disabled:opacity-40 hover:bg-gray-700 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {preview && <MediaPreviewModal target={preview} onClose={() => setPreview(null)} />}
    </div>
  );
};

export default AdminAiGenerationsPage;
